import React, { useEffect } from 'react';
import styles from './ErrorDataSettings.module.scss';
import { Button, CircularProgress, Typography } from '@mui/material';
import { Stack } from 'new/elements/Stack';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { DELETE_QUARANTINE_REQUEST, GET_QUARANTINE_REQUEST } from 'js/constants/actionTypes';
import { useUserQuarantine } from 'new/hooks/adapters/useUserQuarantine';
import { ScreenComponent } from 'new/routes/RouterManager';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import { ROUTE_MONITOR } from 'new/constants/pathnames';
import { useDispatch } from 'react-redux';
import {
  deleteUserQuarantineConfig,
  loadUserQuarantine,
  setContextualSupport,
  unsetContextualSupport,
} from 'js/actions';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { Table } from 'new/elements/Table';
import { quarantineColumns } from 'new/components/monitor/ErrorDataSettings/credentialColumns';
import UIUtils from 'js/lib/UIUtils';
import { CONNECTION_CATEGORY_FILE } from 'js/constants/connectionCategories';
import { useDrawerContext } from 'new/components/drawer/useDrawerContext';
import { filter, isEmpty } from 'lodash';
import Menu from 'js/components/elements/menu/Menu';
import { useHistory } from 'react-router-dom';
import { EditErrorDataSettings } from 'new/components/monitor/EditErrorDataSettings';
import { BannerDescription } from 'new/elements/BannerDescription';
import { SUPPORT_KEY_QUARANTINE } from 'js/constants/supportPageKeys';
import screenConfig from 'new/components/monitor/ErrorDataSettings/screenConfig';

interface ErrorDataSettingsProps {}
const description =
  'Store any record that failed to make its journey from source to destination in a file system of your choice.';

export const ErrorDataSettings: ScreenComponent<ErrorDataSettingsProps> = (
  props: ErrorDataSettingsProps,
) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { drawerContentRef } = useDrawerContext();
  const user = useCurrentUser();
  const loading = useIsLoading(GET_QUARANTINE_REQUEST, DELETE_QUARANTINE_REQUEST);

  const { quarantine, credential, sourceName, sourceIcon, sourceConfigFields } =
    useUserQuarantine();

  useEffect(() => {
    dispatch(loadUserQuarantine(user.id));
    dispatch(setContextualSupport(SUPPORT_KEY_QUARANTINE));
    return function cleanup() {
      dispatch(unsetContextualSupport(SUPPORT_KEY_QUARANTINE));
    };
  }, []);

  const sourceLocation = UIUtils.getLocationFromSourceConfig(
    quarantine?.config,
    CONNECTION_CATEGORY_FILE,
    false,
    null,
    quarantine?.id,
  );

  const cronField = filter(sourceConfigFields, (c) => c.name === 'start.cron').shift();

  const onClickChangeConfig = () => {};
  const onClickDelete = () => {
    dispatch(deleteUserQuarantineConfig(user.id));
  };

  const onConfigure = () => {
    history.push(EditErrorDataSettings.screenConfig.path);
  };

  const rowEndRender = (data) => {
    return (
      <Menu
        horizontalDots
        items={[
          {
            label: 'Actions',
            type: 'heading',
          },
          {
            label: 'Change Configuration',
            disable: isEmpty(data?.quarantine) || isEmpty(data?.credential),
            onClick: onConfigure,
          },
          {
            label: '',
            type: 'heading',
          },
          {
            label: 'Delete',
            color: 'error',
            type: 'dialog',
            confirmLabel: 'Delete',
            discardLabel: 'Cancel',
            dialogMessage:
              'Please confirm you want to stop writing error data files to the configured destination.',
            primaryButtonColor: 'error',
            onClick: onClickDelete,
          },
        ]}
      />
    );
  };

  return (
    <div>
      <BannerDescription title="Error Data Settings" description={description} />
      <div className={styles.container}>
        {loading || !quarantine ? (
          <Stack padding="24px" maxWidth="640px">
            <Typography variant="body2">
              Nexla detects errors during different stages of data flow such as ingestion,
              transformation, and output. Error records are quarantined and accessible to the user
              via APIs as well as files. Configure Nexla to write files containing information about
              erroneous records across all your data flows and Nexla will automatically categorize
              and write all error records with relevant meta data.
            </Typography>
          </Stack>
        ) : null}
        {!loading && !!quarantine && !!sourceLocation ? (
          <>
            <Typography className={styles.tableDescription}>
              Nexla will write error records to the location below
            </Typography>
            <Table
              parentScrollRef={drawerContentRef}
              columns={quarantineColumns}
              rowEndRender={rowEndRender}
              rows={[
                {
                  ...quarantine,
                  quarantine,
                  credential,
                  sourceName,
                  sourceIcon,
                  cronField,
                  sourceLocation,
                },
              ]}
            />
          </>
        ) : null}
        <Stack padding="24px">
          {loading ? (
            <div className={styles.loader}>
              <CircularProgress size={16} />
              <Typography variant="subtitle1">Checking for configuration...</Typography>
            </div>
          ) : null}
          {!loading && !quarantine ? (
            <Stack className={styles.notConfiguredContainer}>
              <Typography variant="body2">
                Nexla has not been configured to write erroneous records automatically.
              </Typography>
              <Button onClick={onConfigure} variant="contained">
                Configure Error Data Settings
              </Button>
            </Stack>
          ) : null}
        </Stack>
      </div>
    </div>
  );
};

ErrorDataSettings.screenConfig = screenConfig;
